// Normalize a raw iTunes Lookup response for one storefront into the common
// row model described in docs/PLAN.md. This is intentionally pure and
// side-effect free so it can be unit tested in isolation.

/**
 * @typedef {object} RegionRow
 * @property {string} countryCode
 * @property {string} countryName
 * @property {boolean} available
 * @property {number|null} trackId
 * @property {string|null} name
 * @property {string|null} sellerName
 * @property {number|null} price
 * @property {string|null} formattedPrice
 * @property {string|null} currency
 * @property {number|null} averageUserRating
 * @property {number|null} userRatingCount
 * @property {string|null} storeUrl
 * @property {string|null} error
 * @property {string|null} fileSize - Human-readable file size, e.g. "142 MB".
 * @property {string|null} contentRating - Age rating string, e.g. "4+", "17+".
 * @property {string|null} minimumOsVersion - Minimum iOS required, e.g. "15.0".
 * @property {unknown[]|null} languageCodesISO2A - Raw Apple languageCodesISO2A list.
 * @property {number|null} languageCount - Number of supported languages.
 * @property {string|null} releaseDate - ISO 8601 date of the current version release.
 * @property {string|null} version - Version string for the current release, e.g. "3.2.1".
 * @property {string|null} releaseNotes - Localized release notes for the current version (trimmed, max 2000 chars).
 * @property {string|null} description - App description (trimmed, max 1000 chars).
 */

/**
 * Normalize a raw iTunes Lookup payload into a region row.
 *
 * Availability rule (per PLAN.md): resultCount === 0 (or no software result)
 * means the app is not available in that storefront.
 *
 * @param {{code: string, name: string}} storefront
 * @param {any} lookupResponse - Raw `{ resultCount, results }` object, or null.
 * @returns {RegionRow}
 */
export function normalizeLookup(storefront, lookupResponse) {
  const base = {
    countryCode: storefront.code,
    countryName: storefront.name,
    available: false,
    trackId: null,
    name: null,
    sellerName: null,
    price: null,
    formattedPrice: null,
    currency: null,
    averageUserRating: null,
    userRatingCount: null,
    storeUrl: null,
    error: null,
    fileSize: null,
    contentRating: null,
    minimumOsVersion: null,
    languageCodesISO2A: null,
    languageCount: null,
    releaseDate: null,
    version: null,
    releaseNotes: null,
    description: null,
  };

  // Malformed / missing response -> unavailable, but not an error by itself.
  if (!lookupResponse || typeof lookupResponse !== 'object') {
    return base;
  }

  const results = Array.isArray(lookupResponse.results)
    ? lookupResponse.results
    : [];

  // Prefer an explicit software result; fall back to the first result.
  const result =
    results.find((r) => r && r.wrapperType === 'software') ?? results[0];

  if (!result) {
    return base;
  }

  return {
    ...base,
    available: true,
    trackId: toNumberOrNull(result.trackId),
    name: nonEmptyStringOrNull(result.trackName),
    sellerName: nonEmptyStringOrNull(result.sellerName),
    price: toNumberOrNull(result.price),
    formattedPrice: nonEmptyStringOrNull(result.formattedPrice),
    currency: nonEmptyStringOrNull(result.currency),
    averageUserRating: toNumberOrNull(result.averageUserRating),
    userRatingCount: toNumberOrNull(result.userRatingCount),
    storeUrl: nonEmptyStringOrNull(result.trackViewUrl),
    fileSize: formatFileSize(toNumberOrNull(result.fileSizeBytes)),
    contentRating: nonEmptyStringOrNull(result.contentAdvisoryRating),
    minimumOsVersion: nonEmptyStringOrNull(result.minimumOsVersion),
    languageCodesISO2A: rawLanguageCodes(result.languageCodesISO2A),
    languageCount: countLanguages(result.languageCodesISO2A),
    releaseDate: isoDateOrNull(result.currentVersionReleaseDate),
    version: nonEmptyStringOrNull(result.version),
    releaseNotes: nonEmptyStringOrNull(result.releaseNotes)?.slice(0, 2000) ?? null,
    description: nonEmptyStringOrNull(result.description)?.slice(0, 1000) ?? null,
  };
}

/**
 * Build an error row for a storefront whose lookup request failed entirely.
 * @param {{code: string, name: string}} storefront
 * @param {string} message
 * @returns {RegionRow}
 */
export function errorRow(storefront, message) {
  const row = normalizeLookup(storefront, null);
  row.error = message || 'Lookup failed';
  return row;
}

/**
 * Format bytes as a human-readable file size string.
 * Returns null when bytes is null/invalid.
 * @param {number|null} bytes
 * @returns {string|null}
 */
export function formatFileSize(bytes) {
  if (bytes === null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes === 0) return '0 B';
  const GB = 1024 * 1024 * 1024;
  const MB = 1024 * 1024;
  const KB = 1024;
  if (bytes >= GB) return `${(bytes / GB).toFixed(1)} GB`;
  if (bytes >= MB) return `${Math.round(bytes / MB)} MB`;
  if (bytes >= KB) return `${Math.round(bytes / KB)} KB`;
  return `${bytes} B`;
}

function countLanguages(value) {
  if (!Array.isArray(value)) return null;
  return value.length > 0 ? value.length : null;
}

function rawLanguageCodes(value) {
  if (!Array.isArray(value)) return null;
  return value.length > 0 ? [...value] : null;
}

function isoDateOrNull(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  // Accept ISO 8601 date strings; strip time component for display.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function nonEmptyStringOrNull(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
